import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, UserPlus, Trash2, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useTeamSpace,
  useUpdateTeamSpace,
  useDeleteTeamSpace,
  useAddTeamSpaceMember,
  useUpdateTeamSpaceMemberRole,
  useRemoveTeamSpaceMember,
} from '../../hooks/useTeamSpaces';
import { useAuthStore } from '../../store/authStore';
import { useOrgStore } from '../../store/orgStore';
import { getOrgMembers, type OrgMember } from '../../services/organizationService';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface TeamSpaceSettingsModalProps {
  spaceId: string;
  open: boolean;
  onClose: () => void;
  // CHG-130 REQ-DHR-027: scope='project' 시 멤버 탭 readonly, 삭제 버튼 숨김
  scope?: 'global' | 'project';
  projectKey?: string;
}

type Tab = 'general' | 'members';

const ROLE_LABELS: Record<string, string> = {
  OWNER: '소유자',
  MANAGER: '관리자',
  MEMBER: '멤버',
};

const ROLE_COLORS: Record<string, string> = {
  OWNER: 'bg-purple-100 text-purple-700',
  MANAGER: 'bg-blue-100 text-blue-700',
  MEMBER: 'bg-gray-100 text-gray-600',
};

export default function TeamSpaceSettingsModal({ spaceId, open, onClose, scope = 'global', projectKey }: TeamSpaceSettingsModalProps) {
  const [tab, setTab] = useState<Tab>('general');
  const isProjectScope = scope === 'project';

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              {isProjectScope ? '프로젝트 문서함 설정' : '팀 스페이스 설정'}
            </h2>
            <div className="flex gap-1">
              <button
                onClick={() => setTab('general')}
                className={`px-3 py-1 text-sm rounded-lg ${tab === 'general' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                일반
              </button>
              <button
                onClick={() => setTab('members')}
                className={`px-3 py-1 text-sm rounded-lg ${tab === 'members' ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                멤버
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {tab === 'general' ? (
            <GeneralTab spaceId={spaceId} onClose={onClose} isProjectScope={isProjectScope} />
          ) : (
            <MembersTab spaceId={spaceId} isProjectScope={isProjectScope} projectKey={projectKey} onClose={onClose} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── General Tab ───
function GeneralTab({ spaceId, onClose, isProjectScope }: { spaceId: string; onClose: () => void; isProjectScope: boolean }) {
  const { data: teamSpace } = useTeamSpace(spaceId);
  const updateSpace = useUpdateTeamSpace();
  const deleteSpace = useDeleteTeamSpace();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'PUBLIC' | 'PRIVATE'>('PRIVATE');

  const myRole = teamSpace?.members?.find((m) => m.user.id === currentUser?.id)?.role ?? null;

  useEffect(() => {
    if (teamSpace) {
      setName(teamSpace.name);
      setDescription(teamSpace.description ?? '');
      setVisibility(teamSpace.visibility);
    }
  }, [teamSpace]);

  const handleSave = async () => {
    if (!name.trim()) return;
    try {
      await updateSpace.mutateAsync({
        spaceId,
        data: { name: name.trim(), description: description.trim() || null, visibility },
      });
      toast.success('설정이 저장되었습니다.');
    } catch {
      toast.error('저장에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!confirm('이 팀 스페이스를 삭제하시겠습니까? 모든 문서가 영구 삭제됩니다.')) return;
    try {
      await deleteSpace.mutateAsync(spaceId);
      toast.success('팀 스페이스가 삭제되었습니다.');
      onClose();
      navigate('/documents');
    } catch {
      toast.error('삭제에 실패했습니다.');
    }
  };

  return (
    <div className="p-6 space-y-4">
      {isProjectScope && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="size-4 shrink-0 mt-0.5" />
          <span>프로젝트 문서함은 프로젝트와 라이프사이클이 연동됩니다. 삭제는 프로젝트 삭제 시 함께 처리됩니다.</span>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={100}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">공개 범위</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" value="PRIVATE" checked={visibility === 'PRIVATE'} onChange={() => setVisibility('PRIVATE')} className="text-primary-600" />
            <span className="text-sm text-gray-700">비공개</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" value="PUBLIC" checked={visibility === 'PUBLIC'} onChange={() => setVisibility('PUBLIC')} className="text-primary-600" />
            <span className="text-sm text-gray-700">공개</span>
          </label>
        </div>
      </div>
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!name.trim() || updateSpace.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {updateSpace.isPending ? '저장 중...' : '저장'}
        </button>
      </div>

      {/* Danger zone — CHG-130: project scope에서는 삭제 불가 */}
      {!isProjectScope && myRole === 'OWNER' && (
        <div className="border-t border-gray-200 pt-4 mt-6">
          <h3 className="text-sm font-semibold text-red-600 mb-2">위험 영역</h3>
          <button
            onClick={handleDelete}
            disabled={deleteSpace.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50"
          >
            <Trash2 className="size-4" />
            {deleteSpace.isPending ? '삭제 중...' : '팀 스페이스 삭제'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Members Tab ───
function MembersTab({ spaceId, isProjectScope, projectKey, onClose }: { spaceId: string; isProjectScope: boolean; projectKey?: string; onClose: () => void }) {
  const { openProfileModal } = useProfileModal();
  const { data: teamSpace } = useTeamSpace(spaceId);
  const currentUser = useAuthStore((s) => s.user);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const navigate = useNavigate();

  const addMember = useAddTeamSpaceMember();
  const updateRole = useUpdateTeamSpaceMemberRole();
  const removeMember = useRemoveTeamSpaceMember();

  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [addRole, setAddRole] = useState<'MANAGER' | 'MEMBER'>('MEMBER');

  const myRole = teamSpace?.members?.find((m) => m.user.id === currentUser?.id)?.role ?? null;

  useEffect(() => {
    if (activeOrgId) {
      getOrgMembers(activeOrgId).then(setOrgMembers).catch(() => {});
    }
  }, [activeOrgId]);

  const existingUserIds = useMemo(
    () => new Set(teamSpace?.members?.map((m) => m.user.id) ?? []),
    [teamSpace?.members],
  );

  const availableMembers = useMemo(() => {
    const filtered = orgMembers.filter((m) => !existingUserIds.has(m.user.id));
    if (!memberSearch.trim()) return filtered;
    const q = memberSearch.toLowerCase();
    return filtered.filter((m) => m.user.name.toLowerCase().includes(q) || m.user.email.toLowerCase().includes(q));
  }, [orgMembers, existingUserIds, memberSearch]);

  const handleAddMember = async (userId: string) => {
    try {
      await addMember.mutateAsync({ spaceId, data: { userId, role: addRole } });
      toast.success('멤버가 추가되었습니다.');
      setShowAddDropdown(false);
      setMemberSearch('');
    } catch {
      toast.error('멤버 추가에 실패했습니다.');
    }
  };

  const handleRoleChange = async (memberId: string, role: 'OWNER' | 'MANAGER' | 'MEMBER') => {
    try {
      await updateRole.mutateAsync({ spaceId, memberId, data: { role } });
      toast.success('역할이 변경되었습니다.');
    } catch {
      toast.error('역할 변경에 실패했습니다.');
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!confirm(`${memberName} 님을 제거하시겠습니까?`)) return;
    try {
      await removeMember.mutateAsync({ spaceId, memberId });
      toast.success('멤버가 제거되었습니다.');
    } catch {
      toast.error('멤버 제거에 실패했습니다.');
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* CHG-130 REQ-DHR-027: project scope 멤버 readonly 안내 */}
      {isProjectScope && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="size-4 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p>프로젝트 멤버 변경은 프로젝트 설정에서 관리하세요. 이 목록은 프로젝트 멤버와 자동 동기화됩니다.</p>
            {projectKey && (
              <button
                onClick={() => { onClose(); navigate(`/projects/${projectKey}/settings`); }}
                className="mt-1 text-sm font-medium text-blue-700 hover:underline"
              >
                프로젝트 설정으로 이동 →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add member — global scope만 */}
      {!isProjectScope && (myRole === 'OWNER' || myRole === 'MANAGER') && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <button
              onClick={() => setShowAddDropdown(!showAddDropdown)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100"
            >
              <UserPlus className="size-4" />
              멤버 추가
            </button>
            {showAddDropdown && (
              <select
                value={addRole}
                onChange={(e) => setAddRole(e.target.value as 'MANAGER' | 'MEMBER')}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              >
                <option value="MEMBER">멤버</option>
                <option value="MANAGER">관리자</option>
              </select>
            )}
          </div>

          {showAddDropdown && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-2">
                <input
                  type="text"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="이름 또는 이메일로 검색..."
                  autoFocus
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="max-h-40 overflow-y-auto">
                {availableMembers.length > 0 ? (
                  availableMembers.map((m) => (
                    <button
                      key={m.user.id}
                      onClick={() => handleAddMember(m.user.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
                    >
                      <Avatar
                        size="xs"
                        userId={m.user.id}
                        name={m.user.name}
                        avatarUrl={m.user.avatarUrl}
                        displayColor={m.user.displayColor as ProfileColorKey | null | undefined}
                        firstName={m.user.firstName}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{m.user.name}</p>
                        <p className="text-sm text-gray-500 truncate">{m.user.email}</p>
                      </div>
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-3 text-sm text-gray-400 text-center">추가할 수 있는 멤버가 없습니다.</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Member list */}
      <div className="space-y-1">
        {teamSpace?.members?.map((member) => (
          <div key={member.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50">
            <Avatar
              size="sm"
              userId={member.user.id}
              name={member.user.name}
              avatarUrl={member.user.avatarUrl}
              displayColor={member.user.displayColor as ProfileColorKey | null | undefined}
              firstName={member.user.firstName}
              onClick={() => openProfileModal(member.user.id)}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{member.user.name}</p>
              <p className="text-sm text-gray-500 truncate">{member.user.email}</p>
            </div>

            {/* Role badge / dropdown — CHG-130: project scope에서는 항상 readonly */}
            {!isProjectScope && myRole === 'OWNER' && member.role !== 'OWNER' ? (
              <select
                value={member.role}
                onChange={(e) => handleRoleChange(member.id, e.target.value as 'OWNER' | 'MANAGER' | 'MEMBER')}
                className={`text-sm font-medium px-2 py-1 rounded-lg border-0 ${ROLE_COLORS[member.role]}`}
              >
                <option value="MEMBER">멤버</option>
                <option value="MANAGER">관리자</option>
                <option value="OWNER">소유자</option>
              </select>
            ) : (
              <span className={`text-sm font-medium px-2 py-1 rounded-lg ${ROLE_COLORS[member.role]}`}>
                {ROLE_LABELS[member.role]}
              </span>
            )}

            {/* Remove button — CHG-130: project scope에서 숨김 */}
            {!isProjectScope && (myRole === 'OWNER' || myRole === 'MANAGER') && member.role !== 'OWNER' && (
              <button
                onClick={() => handleRemoveMember(member.id, member.user.name)}
                className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50"
                title="멤버 제거"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
